// src/screens/MilestonesWealth.tsx
// Split from Milestones.tsx — Panel 1: Wealth Trajectory stacked area chart

import React, { useMemo } from 'react';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceLine, ResponsiveContainer, Legend,
} from 'recharts';
import { ClientData } from '../types/ClientData';
import { formatCurrency } from '../utils/format';
import { POT_COLOURS } from '../constants/potColours';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

const fmt = formatCurrency;

export default function MilestonesWealth({ data, onUpdate }: Props) {
  const clientAge = data.clientAge || 40;
  const freedomAge = data.targetFreedomAge || 55;
  const yearsToProject = Math.max(freedomAge - clientAge + 10, 20);

  const pensionMonthly = (data.pensionCompanyFunded || 0) + (data.planPensionFromDividendReduction || 0);
  const isaMonthly = data.planIsaMonthlyContribution || data.isaContribution || 0;

  const startPension = data.pensionBalance || 0;
  const startISA = data.investmentsBalance || 0;

  // Cash (savings accounts + EF accounts — simple, no growth for cash)
  const startCash =
    (data.efAccount1Balance || 0) +
    (data.efAccount2Balance || 0) +
    (data.savingsAccounts || []).reduce((s: number, a: { balance?: number }) => s + (a.balance || 0), 0);

  // Property equity for the note below the chart
  const propertyEquityToday = Math.max(0,
    (data.propertyValue || 0) - (data.mortgageBalance || 0) +
    (data.rentalPropertyValue || 0) - (data.rentalMortgageBalance || 0)
  );

  // Mortgage amortization inputs (residential)
  const startMortgage = data.mortgageBalance || 0;
  // mortgageRate stored as percentage (e.g. 4.5), convert to decimal
  const mortgageRate = (data.mortgageRate || 0) / 100;
  const mortgagePaymentAnnual =
    ((data.mortgagePayment || 0) + (data.mortgageOP || 0) + (data.planMortgageOverpayment || 0)) * 12 +
    (data.planMortgageAnnualLumpSum || 0);

  const wealthData = useMemo(() => {
    const points: Array<{
      age: number;
      pension: number;
      isa: number;
      cash: number;
      total: number;
      mortgageBalance: number;
    }> = [];

    const pensionGrowth = data.assumptionPensionGrowth || 0.08;
    const isaGrowth = data.assumptionIsaGrowth || 0.08;
    const cashGrowth = data.assumptionCashRate || 0.02;

    let pen = startPension;
    let isa = startISA;
    let cash = startCash;
    let mort = startMortgage;

    for (let yr = 0; yr <= yearsToProject; yr++) {
      const age = clientAge + yr;
      const total = pen + isa + cash;
      points.push({
        age,
        pension: Math.round(pen),
        isa: Math.round(isa),
        cash: Math.round(cash),
        total: Math.round(total),
        mortgageBalance: Math.round(Math.max(0, mort)),
      });

      pen = pen * (1 + pensionGrowth) + pensionMonthly * 12;
      isa = isa * (1 + isaGrowth) + isaMonthly * 12;
      cash = cash * (1 + cashGrowth); // cash grows at savings rate, no new contributions modelled
      // Mortgage amortization: interest accrues, payment reduces balance
      mort = Math.max(0, mort * (1 + mortgageRate) - mortgagePaymentAnnual);
    }

    return points;
  }, [clientAge, yearsToProject, startPension, startISA, startCash, startMortgage,
      pensionMonthly, isaMonthly, mortgageRate, mortgagePaymentAnnual,
      data.assumptionPensionGrowth, data.assumptionIsaGrowth, data.assumptionCashRate]);

  // Find the age at which the mortgage hits zero (for ReferenceLine milestone)
  const mortgageFreeAge = useMemo(() => {
    if (startMortgage <= 0) return null;
    const hit = wealthData.find(p => p.mortgageBalance <= 0);
    return hit ? hit.age : null;
  }, [wealthData, startMortgage]);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Your Wealth Trajectory</h1>
      <p className="text-gray-600 mb-8">
        Watch what happens when the mortgage hits zero.
      </p>
      <p className="text-sm text-slate-400 mb-4">
        Pension and investments compound at {Math.round((data.assumptionPensionGrowth || 0.08) * 100)}%.{' '}
        {mortgageFreeAge !== null
          ? <>Mortgage clears at age <span className="font-semibold text-gray-700">{mortgageFreeAge}</span> — that monthly payment then redirects to your ISA. </>
          : null}
        The gold line is your Freedom Number.
      </p>

      <div className="mb-8 bg-white rounded-2xl border border-gray-200 p-5">
        {(() => {
          // Brief 49 S1 — diagnostic console.log
          // eslint-disable-next-line no-console
          console.log('[Brief49] MilestonesWealth wealthData length:', wealthData.length, 'First 3:', JSON.stringify(wealthData.slice(0, 3)));
          return null;
        })()}
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={wealthData} margin={{ top: 20, right: 30, bottom: 20, left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="age"
              tickFormatter={(age: number) => `${age}`}
              tick={{ fill: '#6b7280', fontSize: 11 }}
            />
            <YAxis
              tickFormatter={(v: number) => `£${(v / 1000).toFixed(0)}k`}
              tick={{ fill: '#6b7280', fontSize: 11 }}
            />
            <Tooltip formatter={(v: any) => `£${Number(v).toLocaleString()}`} />
            <Legend />
            {/* Brief 47 S1 — Areas declared BEFORE ReferenceLine so reference label sits on top */}
            <Area type="monotone" dataKey="pension" stackId="1" stroke={POT_COLOURS.pension} fill={POT_COLOURS.pension} fillOpacity={0.7} name="Pension" />
            <Area type="monotone" dataKey="isa" stackId="1" stroke={POT_COLOURS.investments} fill={POT_COLOURS.investments} fillOpacity={0.5} name="ISA" />
            <Area type="monotone" dataKey="cash" stackId="1" stroke={POT_COLOURS.cash} fill={POT_COLOURS.cash} fillOpacity={0.3} name="Cash" />
            {/* Brief 53 — mortgage balance falling line (Lines AFTER Areas so it renders on top) */}
            {startMortgage > 0 && (
              <Line
                type="monotone"
                dataKey="mortgageBalance"
                stroke="#EF4444"
                strokeWidth={2}
                strokeDasharray="5 4"
                dot={false}
                name="Mortgage balance"
              />
            )}
            <ReferenceLine
              y={data.freedomNumberInflated || 0}
              stroke="#C9A84C"
              strokeWidth={2}
              label={{ value: `Freedom Number — £${((data.freedomNumberInflated || 0)/1000).toFixed(0)}k`, position: 'insideTopLeft', fill: '#C9A84C', fontSize: 13, fontWeight: 700 }}
            />
            {mortgageFreeAge !== null && (
              <ReferenceLine
                x={mortgageFreeAge}
                stroke="#EF4444"
                strokeWidth={2}
                strokeDasharray="3 3"
                label={{ value: `Mortgage-free age ${mortgageFreeAge}`, position: 'top', fill: '#EF4444', fontSize: 12, fontWeight: 700 }}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
        {wealthData.length > 0 && (
          <p className="text-center text-sm text-gray-600 mt-2">
            Projected liquid wealth at age {freedomAge}:{' '}
            <span className="font-bold" style={{ color: '#1a2e5a' }}>
              {fmt(wealthData.find(p => p.age === freedomAge)?.total || 0)}
            </span>
          </p>
        )}
        <p style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 13, marginTop: 8 }}>
          Hover over any year to see your liquid wealth breakdown at that age.
        </p>
        <p className="text-xs text-slate-400 text-center mt-2">
          Assumes {Math.round((data.assumptionPensionGrowth || 0.08) * 100)}% pension/ISA growth and {Math.round((data.assumptionCashRate || 0.02) * 100)}% cash rate. For planning purposes — actual returns will vary.
        </p>
      </div>

      {/* Stat cards */}
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        {[
          { label: 'Pension at freedom', value: fmt(wealthData.find(p => p.age === freedomAge)?.pension || 0) },
          { label: 'ISA at freedom', value: fmt(wealthData.find(p => p.age === freedomAge)?.isa || 0) },
          { label: 'Cash at freedom', value: fmt(wealthData.find(p => p.age === freedomAge)?.cash || 0) },
          { label: 'Total liquid wealth', value: fmt(wealthData.find(p => p.age === freedomAge)?.total || 0) },
        ].map((card, i) => (
          <div key={i} style={{
            flex: '1 1 160px',
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '16px',
          }}>
            <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px' }}>{card.label}</p>
            <p style={{ fontSize: '20px', fontWeight: 700, color: '#001F8C' }}>{card.value}</p>
          </div>
        ))}
      </div>

      {/* 46C S2d — Property equity note */}
      <p className="text-xs text-gray-400 text-center mt-6 max-w-xl mx-auto">
        Property equity (today: {fmt(propertyEquityToday)}) is shown separately in your balance sheet. It contributes to net worth but is not part of your drawdown strategy and is not included here.
      </p>

      <p className="text-sm text-center mt-4" style={{ color: '#9ca3af', fontStyle: 'italic' }}>
        This is what consistent decisions compound to.
      </p>
    </div>
  );
}
